import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Wallet, TrendingDown, PiggyBank, ReceiptText, Pencil, Trash2, Upload, Plus, Check, X, Download, FileText, FileSpreadsheet, Loader2, Eye } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { NFImportDialog } from "@/components/NFImportDialog";
import { BalanceEntryDialog } from "@/components/BalanceEntryDialog";
import { BalanceEntryViewDialog } from "@/components/BalanceEntryViewDialog";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const fmtMonth = (m) => {
  const [y, mo] = (m || "").split("-");
  return mo ? `${MONTHS[parseInt(mo, 10) - 1]}/${y.slice(2)}` : m;
};
const fmtDate = (d) => {
  if (!d) return "-";
  const [y, m, day] = d.slice(0, 10).split("-");
  return day ? `${day}/${m}/${y}` : d;
};

const Donut = ({ pct, color, label }) => {
  const r = 42, c = 2 * Math.PI * r;
  const off = c - (Math.min(pct, 100) / 100) * c;
  return (
    <div className="flex flex-col items-center justify-center">
      <svg width="110" height="110" viewBox="0 0 110 110" className="-rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="hsl(214,32%,91%)" strokeWidth="11" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="-mt-[74px] mb-[34px] text-center">
        <span className="font-heading text-2xl font-black tracking-tighter">{pct}%</span>
      </div>
      <span className="text-xs text-muted-foreground font-medium">{label}</span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, valueClass = "", testId }) => (
  <Card className="border border-border shadow-none">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className={`font-heading text-xl lg:text-2xl font-black tracking-tighter truncate ${valueClass}`} data-testid={testId}>{value}</p>
      </div>
    </CardContent>
  </Card>
);

export const BalanceReport = () => {
  const { can } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/clients`, { withCredentials: true })
      .then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!clientId) { setData(null); return; }
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/reports/balance?client_id=${clientId}`, { withCredentials: true });
      setData(d);
    } catch { console.error("Erro ao carregar controle de saldo"); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const saveBudget = async () => {
    try {
      await axios.put(`${API}/balance/budget`, { client_id: clientId, material_budget: parseFloat(budgetInput) || 0 }, { withCredentials: true });
      toast.success("Previsao de gasto atualizada");
      setEditingBudget(false);
      load();
    } catch { toast.error("Erro ao salvar previsao"); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/balance/entries/${deleteEntry.id}`, { withCredentials: true });
      toast.success("Lancamento removido");
      setDeleteEntry(null);
      load();
    } catch { toast.error("Erro ao remover lancamento"); }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/reports/balance/export?client_id=${clientId}&format=${format}`, {
        withCredentials: true, responseType: "blob",
      });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `extrato_saldo.${format}`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Extrato ${format === "pdf" ? "PDF" : "Excel"} gerado`);
    } catch { toast.error("Erro ao exportar extrato"); }
    finally { setExporting(false); }
  };

  const monthChart = data ? data.by_month.map((m) => ({ name: fmtMonth(m.month), Gasto: m.value })) : [];
  const supplierChart = data ? data.by_supplier.slice(0, 8).map((s) => ({ name: s.supplier.length > 22 ? s.supplier.slice(0, 22) + "…" : s.supplier, Gasto: s.value })) : [];
  const overBudget = data && data.budget > 0 && data.totals.balance < 0;

  return (
    <div className="space-y-6" data-testid="balance-report">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-64" data-testid="balance-client-select"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
          <SelectContent>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {clientId && (
          <div className="flex gap-2 ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={exporting || !data} data-testid="balance-export-btn">
                  {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="balance-export-pdf">
                  <FileText className="h-4 w-4 mr-2" /> Extrato em PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("xlsx")} data-testid="balance-export-xlsx">
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Planilha Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {can("relatorios", "create") && (
            <Button variant="outline" onClick={() => { setEditingEntry(null); setEntryDialogOpen(true); }} data-testid="balance-manual-entry-btn">
              <Plus className="h-4 w-4 mr-1" /> Lancamento Manual
            </Button>
            )}
            {can("relatorios", "create") && (
            <Button onClick={() => setImportOpen(true)} data-testid="balance-import-nf-btn">
              <Upload className="h-4 w-4 mr-1" /> Importar NF
            </Button>
            )}
          </div>
        )}
      </div>

      {!clientId ? (
        <Card className="border border-dashed border-border shadow-none">
          <CardContent className="py-16 text-center text-muted-foreground">Selecione um cliente para ver o controle de saldo.</CardContent>
        </Card>
      ) : loading ? (
        <p className="text-muted-foreground text-sm">Carregando...</p>
      ) : data ? (
        <>
          {overBudget && (
            <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 font-medium" data-testid="balance-over-budget-alert">
              Atencao: o gasto com materiais ultrapassou a previsao acordada em {fmtBRL(Math.abs(data.totals.balance))}.
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border border-border shadow-none lg:col-span-1">
              <CardContent className="p-4 flex justify-around items-center h-full">
                <Donut pct={Math.round(data.totals.pct_used)} color={overBudget ? "hsl(0,84%,60%)" : "hsl(222,80%,45%)"} label="Consumido da Previsao" />
              </CardContent>
            </Card>
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              <Card className="border border-border shadow-none">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 bg-violet-100 text-violet-700">
                    <Wallet className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Previsao de Gasto</p>
                    {editingBudget ? (
                      <div className="flex items-center gap-1 mt-1">
                        <Input type="number" step="0.01" value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)}
                          className="h-8 w-32" autoFocus data-testid="balance-budget-input" />
                        <Button size="icon" className="h-8 w-8" onClick={saveBudget} data-testid="balance-budget-save-btn"><Check className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingBudget(false)}><X className="h-4 w-4" /></Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="font-heading text-xl lg:text-2xl font-black tracking-tighter truncate" data-testid="balance-budget-value">{fmtBRL(data.budget)}</p>
                        {can("relatorios", "edit") && (
                        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                          onClick={() => { setBudgetInput(String(data.budget || "")); setEditingBudget(true); }} data-testid="balance-budget-edit-btn">
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              <StatCard icon={TrendingDown} label="Total Gasto" value={fmtBRL(data.totals.spent)} color="bg-blue-100 text-blue-700" testId="balance-spent-value" />
              <StatCard icon={PiggyBank} label="Saldo Restante" value={fmtBRL(data.totals.balance)}
                color={overBudget ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}
                valueClass={overBudget ? "text-red-600" : "text-emerald-600"} testId="balance-remaining-value" />
              <StatCard icon={ReceiptText} label="Notas Lancadas" value={data.totals.entries_count} color="bg-slate-100 text-slate-700" testId="balance-entries-count" />
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border border-border shadow-none">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Gasto por Mes</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  {monthChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthChart} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(214,32%,91%)" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                        <Tooltip formatter={(v) => fmtBRL(v)} />
                        <Bar dataKey="Gasto" fill="hsl(222,80%,45%)" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center text-muted-foreground py-16">Sem lancamentos</p>}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-border shadow-none">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Gasto por Fornecedor</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64">
                  {supplierChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={supplierChart} layout="vertical" margin={{ left: 60, right: 10 }}>
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={130} />
                        <Tooltip formatter={(v) => fmtBRL(v)} />
                        <Bar dataKey="Gasto" fill="hsl(160,60%,40%)" radius={[0, 2, 2, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-center text-muted-foreground py-16">Sem lancamentos</p>}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Entries table */}
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Lancamentos</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data Emissao</TableHead>
                      <TableHead>NF</TableHead>
                      <TableHead>Fornecedor</TableHead>
                      <TableHead>Itens</TableHead>
                      <TableHead>Frete</TableHead>
                      <TableHead>Valor Total</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead className="w-[130px]">Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.entries.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">Nenhum lancamento. Importe uma NF ou faca um lancamento manual.</TableCell></TableRow>
                    ) : data.entries.map((e) => (
                      <TableRow key={e.id} data-testid={`balance-entry-row-${e.id}`} className="cursor-pointer" onClick={() => setViewEntry(e)}>
                        <TableCell className="text-sm">{fmtDate(e.nf_date)}</TableCell>
                        <TableCell className="text-sm font-mono">{e.nf_number || "-"}</TableCell>
                        <TableCell className="text-sm font-medium max-w-[240px] truncate">{e.supplier || "-"}</TableCell>
                        <TableCell className="text-sm">{(e.items || []).length}</TableCell>
                        <TableCell className="text-sm">{e.freight ? fmtBRL(e.freight) : "-"}</TableCell>
                        <TableCell className="text-sm font-semibold">{fmtBRL(e.total_value)}</TableCell>
                        <TableCell>
                          <Badge variant={e.source === "nf" ? "default" : "secondary"} className="text-xs">
                            {e.source === "nf" ? "NF Importada" : "Manual"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1" onClick={(ev) => ev.stopPropagation()}>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Visualizar"
                              onClick={() => setViewEntry(e)} data-testid={`balance-entry-view-${e.id}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {can("relatorios", "edit") && (
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar"
                              onClick={() => { setEditingEntry(e); setEntryDialogOpen(true); }} data-testid={`balance-entry-edit-${e.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            )}
                            {can("relatorios", "delete") && (
                            <Button size="icon" variant="ghost" className="h-8 w-8"
                              onClick={() => setDeleteEntry(e)} data-testid={`balance-entry-delete-${e.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}

      <NFImportDialog open={importOpen} onOpenChange={setImportOpen} clients={clients} onImported={load} defaultClientId={clientId} />
      <BalanceEntryDialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen} clientId={clientId} entry={editingEntry} onSaved={load} />
      <BalanceEntryViewDialog entry={viewEntry} onOpenChange={(v) => !v && setViewEntry(null)} />

      <AlertDialog open={!!deleteEntry} onOpenChange={(v) => !v && setDeleteEntry(null)}>
        <AlertDialogContent data-testid="balance-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover lancamento?</AlertDialogTitle>
            <AlertDialogDescription>
              O lancamento da NF {deleteEntry?.nf_number || "sem numero"} ({fmtBRL(deleteEntry?.total_value)}) sera removido do controle de saldo. Esta acao nao afeta o estoque.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="balance-delete-confirm-btn">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
